import { useState } from 'react';

import {
  Modal,
  Paper,
  TextField,
  Grid,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import { Container } from 'types';
import { calculateContainerVolumeWeight } from 'helpers';
import { isValidPositiveFloatNumber } from 'helpers/formatNumber';
import CustomNumberInput from 'components/CustomNumberInput';

type Props = {
  open: boolean;
  handleOpen: () => void;
  onCreate: (newContainer: Container) => void;
};

export default function AddContainerModal({
  open = true,
  handleOpen,
  onCreate,
}: Props) {
  const defaultValues: Omit<Container, 'id'> = {
    description: '',
    m3_min: 0,
    m3_max: 0,
    volume_weight: 0,
    is_active: true,
  };

  const [data, setData] = useState<Omit<Container, 'id'>>(defaultValues);

  const handleChange = <K extends keyof Omit<Container, 'id'>>(
    name: K,
    value: Omit<Container, 'id'>[K]
  ) => {
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleNumberChange = <K extends keyof Omit<Container, 'id'>>(
    name: K,
    rawValue: string
  ) => {
    setData(prev => ({
      ...prev,
      [name]: rawValue,
    }));
  }; 

  const computedVolumeWeight = calculateContainerVolumeWeight(data.m3_max);

  const onSubmit = async (newContainer: Omit<Container, 'id'>) => {
    onCreate(
      {
        ...newContainer,
        volume_weight: calculateContainerVolumeWeight(newContainer.m3_max),
      } as Container,
    );
    setData(defaultValues);
    handleOpen();
  };

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { sm: '90%', md: '30rem' },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant='h5' gutterBottom>
            Nuevo Contenedor
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label='Descripción'
              required
              type='text'
              fullWidth
              onChange={e => handleChange('description', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label='M3 mínimo'
              required
              value={data.m3_min}
              allowOnlyPositive
              onChange={e => handleNumberChange('m3_min', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomNumberInput
              label='M3 máximo'
              required
              value={data.m3_max}
              allowOnlyPositive
              onChange={e => handleNumberChange('m3_max', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label='Peso volumétrico'
              fullWidth
              disabled
              value={computedVolumeWeight}
              helperText='Calculado automáticamente: m3_max × 350'
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Contenedor activo"
            />
          </Grid>
          <Grid item xs={12} display='flex' justifyContent='end' gap={1}>
            <Button
              type='button'
              variant='outlined'
              onClick={() => {
                setData(defaultValues);
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={
                !isValidPositiveFloatNumber(data.m3_min?.toString() || '') ||
                !isValidPositiveFloatNumber(data.m3_max?.toString() || '') ||
                Number(data.m3_max) < Number(data.m3_min)
              }
              onClick={() => {
                onSubmit(data);
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
